import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertCircle,
  FileText,
  Loader2,
  Paperclip,
  Send,
  X,
} from "lucide-react";
import { TRPCClientError } from "@trpc/client";
import { useCallback, useRef, useState } from "react";
import { useTRPC } from "@/trpc";
import { useMutation } from "@tanstack/react-query";
import { tusUploadFile } from "@/ajax/upload/tusUploadFile";
import { declareUploadFile } from "@/ajax/upload/declare-upload-file";
import { contentTypeFor } from "@/common/utils/upload-content-type";
import { SendFormDialog } from "./send-form-dialog";
import { cn } from "@/lib/utils";

const ALLOWED_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/heif",
  "image/webp",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
  "text/plain",
];

const ACCEPT_ATTRIBUTE =
  ".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,.docx,.xlsx,.csv,.txt";

const MAX_ATTACHMENTS = 5;
const MAX_SIZE_BYTES = 20 * 1024 * 1024;

type PendingAttachment = {
  id: string;
  fileName: string;
  status: "uploading" | "ready" | "error";
};

export const ChatMessageInput = ({
  onSend,
  readOnly = false,
  disabled = false,
  threadId,
  organizationBusinessUnitId,
  recipientUserId,
  canManageJobPosts = false,
  documentSigningEnabled = false,
  onFormAssignmentSent,
  initialMessage = "",
}: {
  onSend: (message: string, attachmentIds?: string[]) => Promise<void>;
  readOnly: boolean;
  /** True while an unresolved draft form assignment blocks the composer. */
  disabled?: boolean;
  threadId: string;
  /** Present only for the org-side viewer — gates the "Send a form" menu entry. */
  organizationBusinessUnitId?: string;
  recipientUserId?: string;
  canManageJobPosts?: boolean;
  /** Org-side BU feature flag — required for the "Send a form" menu entry. */
  documentSigningEnabled?: boolean;
  onFormAssignmentSent?: (chatThreadId: string) => void;
  initialMessage?: string;
}) => {
  const trpc = useTRPC();
  const [message, setMessage] = useState(initialMessage);
  const [sendError, setSendError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<
    PendingAttachment[]
  >([]);
  const [isDragging, setIsDragging] = useState(false);
  const [sendFormOpen, setSendFormOpen] = useState(false);
  const dragCounterRef = useRef(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canSendForm = Boolean(
    organizationBusinessUnitId &&
      recipientUserId &&
      canManageJobPosts &&
      documentSigningEnabled,
  );

  const createAttachmentMutation = useMutation(
    trpc.chat.createAttachment.mutationOptions(),
  );

  const hasContent =
    message.trim().length > 0 ||
    pendingAttachments.some((a) => a.status === "ready");
  const isUploading = pendingAttachments.some((a) => a.status === "uploading");
  const isDisabled = isSending || isUploading || disabled;

  const handleSend = async (
    e:
      | React.KeyboardEvent<HTMLInputElement>
      | React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    inputRef.current?.focus();
    if (!hasContent || readOnly || isDisabled) {
      return;
    }

    const readyAttachmentIds = pendingAttachments
      .filter((a) => a.status === "ready")
      .map((a) => a.id);

    const newMessage = message.trim();
    setSendError(null);
    setIsSending(true);

    try {
      await onSend(
        newMessage,
        readyAttachmentIds.length > 0 ? readyAttachmentIds : undefined,
      );
      setMessage("");
      setPendingAttachments([]);
    } catch (error) {
      console.error("Failed to send message:", error);

      if (error instanceof TRPCClientError && error.data) {
        setSendError(`Failed to send message: ${error.message}`);
      } else {
        setSendError(
          "Failed to send message. Check your connection and try again.",
        );
      }

      throw error;
    } finally {
      setIsSending(false);
    }
  };

  const uploadFile = async (file: File) => {
    const tempId = crypto.randomUUID();
    setPendingAttachments((prev) => [
      ...prev,
      { id: tempId, fileName: file.name, status: "uploading" },
    ]);

    try {
      const declared = await declareUploadFile(file);
      const attachment = await createAttachmentMutation.mutateAsync({
        chatThreadId: threadId,
        ...declared,
      });

      await tusUploadFile({
        file,
        bucketName: attachment.bucketName,
        objectName: attachment.storagePath,
        uploadToken: attachment.uploadToken,
      });

      setPendingAttachments((prev) =>
        prev.map((a) =>
          a.id === tempId
            ? { id: attachment.id, fileName: file.name, status: "ready" }
            : a,
        ),
      );
    } catch (error) {
      console.error("Failed to upload file:", error);
      setPendingAttachments((prev) =>
        prev.map((a) => (a.id === tempId ? { ...a, status: "error" } : a)),
      );
      if (error instanceof TRPCClientError && error.data) {
        setSendError(`Failed to upload ${file.name}: ${error.message}`);
      } else if (error instanceof Error) {
        setSendError(`Failed to upload ${file.name}: ${error.message}`);
      }
    }
  };

  const processFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) {
        return;
      }

      const currentCount = pendingAttachments.length;
      const slotsAvailable = MAX_ATTACHMENTS - currentCount;
      if (slotsAvailable <= 0) {
        setSendError(`Maximum ${MAX_ATTACHMENTS} files per message.`);
        return;
      }

      const filesToUpload = files.slice(0, slotsAvailable);
      if (files.length > slotsAvailable) {
        setSendError(
          `Only ${slotsAvailable} more file${slotsAvailable === 1 ? "" : "s"} can be attached. Some files were skipped.`,
        );
      }

      for (const file of filesToUpload) {
        if (!ALLOWED_MIME_TYPES.includes(contentTypeFor(file))) {
          setSendError(
            `"${file.name}" has an unsupported type. Allowed: PDF, images, Word, Excel, CSV, text.`,
          );
          continue;
        }
        if (file.size > MAX_SIZE_BYTES) {
          setSendError(
            `"${file.name}" is too large. Maximum file size is 20 MB.`,
          );
          continue;
        }
        uploadFile(file);
      }
    },
    [pendingAttachments.length, threadId],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    processFiles(files);
  };

  // While the "Send a form" dialog is open it owns any file drop. That dialog is
  // a React child of this composer, so Radix's portal notwithstanding, React
  // synthetic drag events bubble here — swallow them (still preventing the
  // browser's default navigate-to-file) so a PDF meant for the dialog never gets
  // attached to the chat behind it.
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (sendFormOpen) {
      return;
    }
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes("Files")) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (sendFormOpen) {
      return;
    }
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (sendFormOpen) {
      return;
    }
    dragCounterRef.current = 0;
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData.files);
    if (files.length > 0) {
      e.preventDefault();
      processFiles(files);
    }
  };

  const removeAttachment = (id: string) => {
    setPendingAttachments((prev) => prev.filter((a) => a.id !== id));
    setSendError(null);
  };

  const handleAttachClick = () => {
    if (isDisabled) {
      return;
    }
    fileInputRef.current?.click();
  };

  if (readOnly) {
    return null;
  }

  return (
    <div
      className="relative min-w-0 shrink-0 border-t border-slate-200 bg-white p-3 sm:p-4"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl border-2 border-dashed border-primary bg-primary/5">
          <div className="text-center">
            <Paperclip className="h-8 w-8 text-primary mx-auto mb-2" />
            <p className="text-sm font-medium text-primary">
              Drop files to attach
            </p>
          </div>
        </div>
      )}
      {sendError && (
        <div className="mb-3 flex min-w-0 items-start rounded-xl border border-red-200 bg-red-50 p-3 text-sm leading-5 text-red-700 [overflow-wrap:anywhere]">
          <AlertCircle className="h-4 w-4 mr-2 text-red-600 flex-shrink-0" />
          <span>{sendError}</span>
        </div>
      )}
      {pendingAttachments.length > 0 && (
        <div className="mb-3 flex max-h-40 min-w-0 flex-wrap gap-2 overflow-y-auto">
          {pendingAttachments.map((attachment) => (
            <div
              key={attachment.id}
              className={`flex max-w-full min-w-0 items-center gap-2 rounded-xl border pl-3 pr-1 text-sm ${
                attachment.status === "error"
                  ? "bg-red-50 border-red-200 text-red-700"
                  : attachment.status === "uploading"
                    ? "bg-primary/5 border-primary/15 text-primary"
                    : "bg-slate-50 border-slate-200 text-slate-700"
              }`}
            >
              {attachment.status === "uploading" && (
                <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
              )}
              <span className="min-w-0 flex-1 py-2 leading-5 [overflow-wrap:anywhere]">
                {attachment.fileName}
              </span>
              <Button
                type="button"
                variant="ghost"
                aria-label={`Remove ${attachment.fileName}`}
                onClick={() => removeAttachment(attachment.id)}
                className="h-12 w-12 shrink-0 px-0 text-slate-500 hover:bg-red-50 hover:text-red-700"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_ATTRIBUTE}
        multiple
        className="hidden"
        onChange={handleFileChange}
        aria-label="Attach files"
      />
      <div
        className={cn(
          "grid min-w-0 grid-cols-[3rem_minmax(0,1fr)_3rem] items-end gap-2",
          disabled && "opacity-60 pointer-events-none",
        )}
      >
        {canSendForm ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-12 w-12 shrink-0 rounded-xl border border-slate-400 bg-white text-slate-600 hover:border-slate-500 hover:bg-slate-50 hover:text-primary"
                disabled={isDisabled}
                title="Attach"
                aria-label="Attach a document or send a form"
                type="button"
              >
                <Paperclip className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              collisionPadding={16}
              className="max-h-[var(--radix-dropdown-menu-content-available-height)] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-xl border-slate-200 bg-white p-1.5"
            >
              <DropdownMenuItem
                className="min-h-12 gap-2 rounded-lg text-slate-700 focus:bg-primary/5 focus:text-primary"
                onClick={handleAttachClick}
                disabled={
                  isDisabled || pendingAttachments.length >= MAX_ATTACHMENTS
                }
              >
                <Paperclip className="mr-2 h-4 w-4" />
                Attach a document
              </DropdownMenuItem>
              <DropdownMenuItem
                className="min-h-12 gap-2 rounded-lg text-slate-700 focus:bg-primary/5 focus:text-primary"
                onClick={() => setSendFormOpen(true)}
                disabled={isDisabled}
              >
                <FileText className="mr-2 h-4 w-4" />
                Send a form
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button
            size="icon"
            variant="ghost"
            className="h-12 w-12 shrink-0 rounded-xl border border-slate-400 bg-white text-slate-600 hover:border-slate-500 hover:bg-slate-50 hover:text-primary"
            onClick={handleAttachClick}
            disabled={
              isDisabled || pendingAttachments.length >= MAX_ATTACHMENTS
            }
            title="Attach files"
            aria-label="Attach files"
            type="button"
          >
            <Paperclip className="h-5 w-5" />
          </Button>
        )}
        <Textarea
          ref={inputRef}
          className="h-12 min-h-12 min-w-0 resize-none py-2.5 text-base leading-6"
          aria-label="Message"
          placeholder="Message..."
          value={message}
          rows={1}
          onChange={(e) => setMessage(e.target.value)}
          onPaste={handlePaste}
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.shiftKey) {
              e.preventDefault();
              handleSend(e as unknown as React.KeyboardEvent<HTMLInputElement>);
            }
          }}
          disabled={isSending || disabled}
        />
        <Button
          size="icon"
          type="button"
          aria-label="Send message"
          className="h-12 w-12 shrink-0 rounded-xl"
          onClick={handleSend}
          disabled={isDisabled || !hasContent}
        >
          {isSending ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Send className="h-5 w-5" />
          )}
        </Button>
      </div>
      {canSendForm && (
        <SendFormDialog
          open={sendFormOpen}
          onOpenChange={setSendFormOpen}
          organizationBusinessUnitId={organizationBusinessUnitId!}
          recipientUserId={recipientUserId!}
          onSent={(chatThreadId) => {
            setSendFormOpen(false);
            onFormAssignmentSent?.(chatThreadId);
          }}
        />
      )}
    </div>
  );
};
